// Hook global pour gérer l'état de l'entreprise avec persistance
import { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { Company } from '../types/company';
import { useUser } from '../hooks/useUser';
import { CompanyPersistence } from '../utils/companyPersistence';
import companyApiService from '../services/companyApi';

interface CompanyContextType {
  company: Company | null;
  loading: boolean;
  error: string | null;
  setCompany: (company: Company | null) => void;
  refreshCompany: () => Promise<void>;
  clearCompany: () => void;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

interface CompanyProviderProps {
  children: ReactNode;
}

export function CompanyProvider({ children }: CompanyProviderProps) {
  const [company, setCompanyState] = useState<Company | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useUser();
  const initFetchedRef = useRef<string | null>(null);

  // Charger depuis le cache au démarrage, puis API si cache manquant
  useEffect(() => {
    if (!user?.id || !user?.companyId) return;

    const cachedCompany = CompanyPersistence.load(user.id);
    if (cachedCompany && cachedCompany.id === user.companyId) {
      console.log('🏢 Entreprise chargée depuis le cache global:', cachedCompany.name);
      setCompanyState(cachedCompany);
      return;
    }

    // Cache manquant ou périmé → appel API
    if (initFetchedRef.current === user.companyId) return; // déjà en cours
    initFetchedRef.current = user.companyId;

    setLoading(true);
    companyApiService.getCompany(user.companyId)
      .then((apiCompany) => {
        if (apiCompany) {
          console.log('🌐 Entreprise chargée depuis l\'API:', (apiCompany as Company).name);
          setCompanyState(apiCompany as Company);
          CompanyPersistence.save(apiCompany as Company, user.id);
        }
      })
      .catch((err) => {
        console.error('❌ Erreur chargement entreprise au démarrage:', err);
        setError(err instanceof Error ? err.message : 'Erreur inconnue');
      })
      .finally(() => setLoading(false));
  }, [user?.id, user?.companyId]);

  const setCompany = (newCompany: Company | null) => {
    setCompanyState(newCompany);
    
    // Sauvegarder en cache si on a un utilisateur
    if (newCompany && user?.id) {
      CompanyPersistence.save(newCompany, user.id);
    } else if (!newCompany && user?.id) {
      CompanyPersistence.clear();
    }
  };

  const refreshCompany = async () => {
    if (!user?.companyId) return;

    setLoading(true);
    setError(null);

    try {
      const apiCompany = await companyApiService.getCompany(user.companyId);
      if (apiCompany) {
        setCompanyState(apiCompany as Company);
        if (user.id) CompanyPersistence.save(apiCompany as Company, user.id);
      }
    } catch (err) {
      // Fallback sur le cache localStorage
      const cachedCompany = CompanyPersistence.load(user.id);
      if (cachedCompany) setCompanyState(cachedCompany);
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  const clearCompany = () => {
    setCompanyState(null);
    if (user?.id) {
      CompanyPersistence.clear();
    }
  };

  const value: CompanyContextType = {
    company,
    loading,
    error,
    setCompany,
    refreshCompany,
    clearCompany
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompanyContext() {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompanyContext must be used within a CompanyProvider');
  }
  return context;
}

// Hook optionnel qui peut utiliser le contexte global ou le hook local
export function useCompanyProfile(companyId?: string) {
  const { user } = useUser();
  const context = useCompanyContext();
  
  // Si on a un contexte global et que l'ID correspond, utiliser le contexte
  if (context.company?.id === (companyId || user?.companyId)) {
    return {
      company: context.company,
      loading: context.loading,
      error: context.error,
      refreshCompany: context.refreshCompany
    };
  }
  
  // Sinon, charger depuis le cache
  const cachedCompany = user?.id ? CompanyPersistence.load(user.id) : null;
  
  return {
    company: cachedCompany,
    loading: false,
    error: null,
    refreshCompany: context.refreshCompany
  };
}
